export interface StockCountItem {
  inventoryItemId: number;
  inventoryItemName: string;
  unit: string;
  systemQuantity: number;
  actualQuantity: number | null;
  variance: number | null;
}

export interface StockCountSession {
  id: number;
  countDate: string;
  status: string;
  items: StockCountItem[];
}

const UNIT_LABELS: Record<string, string> = {
  KG: 'kg',
  GRAMS: 'g',
  LITRE: 'L',
  ML: 'ml',
  PIECES: 'pcs',
  UNITS: 'units',
};

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string),
  );

export const STOCK_COUNT_PHOTO_INSTRUCTION =
  'Photograph the whole page and send it to the SOG assistant (Telegram or web chat)';

/**
 * Build the printable A4 stock-count worksheet HTML.
 *
 * The Item ID column is rendered in a large monospace font so the assistant's
 * vision model can reliably map each handwritten count back to an inventory
 * item; the Counted Qty column is a wide, thick-bordered cell for handwriting.
 */
export const buildStockCountSheetHtml = (
  session: StockCountSession,
  vendorName?: string,
): string => {
  const dateStr = session.countDate
    ? new Date(session.countDate).toLocaleDateString()
    : new Date().toLocaleDateString();

  const rows = session.items
    .map(
      (item, idx) => `
        <tr>
          <td class="num">${idx + 1}</td>
          <td class="item-id">${item.inventoryItemId}</td>
          <td class="item-name">${escapeHtml(item.inventoryItemName)}</td>
          <td class="unit">${escapeHtml(UNIT_LABELS[item.unit] || item.unit)}</td>
          <td class="count-cell"></td>
        </tr>`,
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Stock Count #${session.id}</title>
        <style>
          @page { size: A4; margin: 14mm; }
          * { box-sizing: border-box; }
          body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; }
          .title { font-size: 32px; font-weight: 800; letter-spacing: 0.5px; margin: 0 0 4px; }
          .vendor { font-size: 18px; font-weight: 600; color: #222; margin: 0; }
          .date { font-size: 14px; color: #555; margin: 2px 0 12px; }
          .instruction {
            font-size: 14px; font-weight: 600; color: #0b5;
            border: 2px dashed #0b5; border-radius: 6px;
            padding: 8px 12px; margin: 0 0 16px;
          }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #333; padding: 8px 10px; }
          th {
            font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em;
            background: #f2f2f2; text-align: left;
          }
          td.num { text-align: center; width: 36px; color: #555; }
          td.item-id {
            font-family: 'Courier New', monospace;
            font-size: 22px; font-weight: 700; width: 90px; text-align: center;
          }
          td.item-name { font-size: 15px; }
          td.unit { font-size: 14px; color: #444; width: 70px; }
          td.count-cell { width: 160px; height: 44px; border: 3px solid #000; }
          @media print { .instruction { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1 class="title">STOCK COUNT #${session.id}</h1>
        ${vendorName ? `<p class="vendor">${escapeHtml(vendorName)}</p>` : ''}
        <p class="date">${dateStr}</p>
        <p class="instruction">${STOCK_COUNT_PHOTO_INSTRUCTION}</p>
        <table>
          <thead>
            <tr>
              <th style="text-align:center">#</th>
              <th style="text-align:center">Item ID</th>
              <th>Item</th>
              <th>Unit</th>
              <th style="text-align:center">Counted Qty</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <script>window.onload = function () { window.print(); }</script>
      </body>
    </html>`;
};
